<template>
  <div class="invoice-details fade-in">
    <!-- Header -->
    <ProductDetailsHeader
        title="Invoice"
        :main="invoice"
        :header-details="headerDetails"
        icon="receipt"
    >
      <template #header-actions>
        <button
            v-if="invoice.status !== 'complete' && invoiceDetails.length > 0"
            @click="completeInvoice"
            class="btn btn-success text-uppercase">
          <i class="bi bi-check-circle me-1"></i> Complete Invoice
        </button>
      </template>
    </ProductDetailsHeader>

    <!-- Available Products Section -->
    <StoreProducts
        v-if="invoiceDetails.length === 0 || showAvailable"
        class="mb-4"
        :per-page="5"
        :marked-products="invoiceDetails"
        @row-click="populateAddProduct"
    />

    <!-- Invoice Products -->
    <ProductDetailsPages
        title="Invoice"
        :loading="loading"
        :detail-products="invoiceDetails"
        :current-page="detailsPage"
        :current-per-page="detailsPerPage"
        :current-search="detailsSearch"
        @update:page="onDetailsPageChange"
        @update:per-page="onDetailsPerPageChange"
        @update:search="onDetailsSearchChange"
        :main="invoice"
        :pdf-org-title="invoice.customer.name"
        :pdf-org-info="pdfOrgInfo"
        @edit-product="editInvoiceProduct"
        @delete-product="deleteInvoiceProduct"
    >
      <template #footer-actions>
        <button class="btn btn-outline-success" v-if="invoiceDetails.length>0 && invoice.status!=='complete'" @click="showAvailable=true">
          <i class="bi bi-plus-circle me-1"></i>Add Invoice Products
        </button>
      </template>
    </ProductDetailsPages>

    <!-- Add/Edit Product Modal -->
    <Modal :open="addProductModal" @closed="addProductModal=false" dialog-size="modal-lg">
      <div class="modal-header">
        <h5 class="modal-title m-0" v-if="addProductMode === 'add'">
          <i class="bi bi-plus-circle me-2 text-primary"></i>Add Product to Invoice
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2 text-primary"></i>Edit Invoice Product
        </h5>
        <button @click="addProductModal=false" type="button" class="btn-close"></button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="addProductMode === 'add' ? addInvoiceProduct() : updateInvoiceProduct()">
          <div class="row">
            <div class="form-group col-md-6 mb-3">
              <label class="form-label fw-medium mb-2">Product Name:</label>
              <input type="text" class="form-control" :value="selectedProduct.product.name" disabled>
            </div>
            <div class="form-group col-md-6 mb-3">
              <label class="form-label fw-medium mb-2">Quantity:</label>
              <div class="input-group">
                <input type="number" class="form-control" autofocus="autofocus" v-model.number="invoiceItem.quantity" v-on:change="calculateTax" min="1" required>
                <span class="input-group-text" v-if="selectedProduct.unit">{{ selectedProduct.unit }}</span>
              </div>
            </div>
            <div class="form-group col-md-6 mb-3">
              <label class="form-label fw-medium mb-2">Price with tax:</label>
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input type="number" class="form-control" v-model.number="invoiceItem.price" v-on:change="calculateTax" min="0" step="0.01" required>
              </div>
              <small class="form-text text-muted">Price without tax: {{ formatNumber((invoiceItem.price * invoiceItem.quantity) - invoiceItem.tax) }}</small>
            </div>
            <div class="form-group col-md-6 mb-3">
              <label class="form-label fw-medium mb-2">Tax:</label>
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input type="number" class="form-control" v-model.number="invoiceItem.tax" min="0" step="0.01" disabled>
              </div>
            </div>
            <div class="form-group col-md-6 mb-3">
              <label class="form-label fw-medium mb-2">Total:</label>
              <div class="alert alert-info py-2 mb-0">
                <i class="bi bi-info-circle me-2"></i>
                {{ formatNumber(invoiceItem.quantity*invoiceItem.price) }}
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button class="btn btn-outline-secondary" @click="addProductModal=false">Cancel</button>
        <button class="btn btn-primary" @click="addProductMode === 'add' ? addInvoiceProduct() : updateInvoiceProduct()">
          <i :class="addProductMode === 'add' ? 'bi bi-plus-lg' : 'bi bi-check-lg'" class="me-1"></i>
          {{ addProductMode === 'add' ? 'Add Product' : 'Update Product' }}
        </button>
      </div>
    </Modal>
    <!-- End Modal -->
  </div>
</template>

<script>
import invoiceService from '@/api/invoiceService'
import formatNumber from '@/utils/formatNumber'
import formatDate from '@/utils/formatDate'
import Badge from '@/components/badge/Badge'
import Spinner from '@/components/spinner/Spinner'
import Modal from '@/components/modal/Modal'
import StoreProducts from '@/components/store/StoreProducts.vue'
import ProductDetailsPages from '@/components/details/ProductDetailsPages.vue'
import confirmDialog from "@/utils/confirmDialog";
import exportToPDF from "@/utils/exportToPDF";
import ProductDetailsHeader from "@/components/details/ProductDetailsHeader.vue";

export default {
  name: 'InvoiceDetails',
  components:{ProductDetailsHeader, Badge, Spinner, Modal, StoreProducts, ProductDetailsPages },
  data(){
    return {
      loading:true,
      loadingProducts:true,
      invoice:{
        customer:{},
        store:{},
        user:{},
      },
      invoiceDetails: { data: [] },
      detailsPage: 1,
      detailsPerPage: 20,
      detailsSearch: '',
      showAvailable:false,
      selectedProduct:{},
      addProductModal:false,
      addProductMode: 'add',
      invoiceItem:{
        invoice_id:this.$route.params.id,
        product_id:'',
        quantity:1,
        price:0,
        tax_rate:0,
        tax:0
      },
      editingProductId: null,
    }
  },
  methods:{
    confirmDialog,
    formatDate,
    formatNumber,
    async fetchInvoice(){
      try{
        const id = this.$route.params.id;
        this.invoice = await invoiceService.getInvoice(id);
        await this.fetchInvoiceDetails();
      }catch(e){ console.error(e);} finally { this.loading = false; }
    },
    async fetchInvoiceDetails(){
      try{
        const id = this.$route.params.id;
        this.invoiceDetails = await invoiceService.getInvoiceDetails(id, {
          page: this.detailsPage,
          per_page: this.detailsPerPage
        });
      }catch(e){ console.error(e);}
    },
    onDetailsPageChange(page){
      this.detailsPage = page;
      this.fetchInvoiceDetails();
    },
    onDetailsPerPageChange(perPage){
      this.detailsPerPage = perPage;
      this.detailsPage = 1;
      this.fetchInvoiceDetails();
    },
    onDetailsSearchChange(search){
      this.detailsSearch = search;
      this.detailsPage = 1;
      this.fetchInvoiceDetails();
    },
    getPillByStatus(status){
      switch (status) {
        case 'complete':
          return 'success';
        default:
          return 'warning';
      }
    },
    populateAddProduct(storeProduct){
      this.selectedProduct = storeProduct;
      this.invoiceItem.product_id = storeProduct.product.id;
      this.invoiceItem.price = storeProduct.price;
      const taxRate = storeProduct.product.tax_category?.rate || 0;
      this.invoiceItem.tax_rate = taxRate;
      this.invoiceItem.tax = (taxRate / 100) * storeProduct.price * this.invoiceItem.quantity;
      this.addProductMode = 'add';
      this.editingProductId = null;
      this.addProductModal=true;
    },
    async addInvoiceProduct(){
      try{
        await invoiceService.createInvoiceDetails(this.invoiceItem);
        this.addProductModal=false;
        this.showAvailable=false;
        await this.fetchInvoice();
      }
      catch(e){console.error(e);}
    },
    async updateInvoiceProduct(){
      try{
        if(this.editingProductId) {
          await invoiceService.updateInvoiceDetails(this.editingProductId, this.invoiceItem);
          this.addProductModal=false;
          this.showAvailable=false;
          await this.fetchInvoice();
        }
      }catch(e){console.error(e);}
    },
    editInvoiceProduct(item){
      this.addProductMode = 'edit';
      this.editingProductId = item.id;
      this.selectedProduct = item;
      this.invoiceItem = {
        invoice_id:this.$route.params.id,
        product_id:item.product.id,
        quantity:item.quantity,
        price:item.price,
        tax_rate:item.product.tax_category.rate,
        tax:item.tax
      };
      this.addProductModal = true;
    },
    async deleteInvoiceProduct(id){
      try{
        await invoiceService.deleteInvoiceDetails(id);
        await this.fetchInvoice();
      }catch(e){console.error(e);}
    },
    calculateTax() {
      this.invoiceItem.tax = (this.invoiceItem.tax_rate / 100) * this.invoiceItem.price * this.invoiceItem.quantity;
    },
    completeInvoice() {
      confirmDialog('Complete Invoice', this.completeInvoiceCall, 'Are you sure you want to complete this invoice')
    },
    async completeInvoiceCall() {
      try {
        const result = await invoiceService.completeInvoice(this.invoice.id);
        if (result) {
          await this.fetchInvoice();
        }
      } catch (e) {
        console.error(e);
      }
    }
  },
  computed: {
    headerDetails() {
      return [
        { icon: 'person', label: 'Customer', key: 'customer.name' },
        { icon: 'calendar-date', type: 'date', label: 'Due Date', key: 'due_date' },
        { icon: 'dollar-sign', type: 'status', label: 'Payment Status', key: 'payment_status' },
        { icon: 'card-text', label: 'Notes', key: 'notes' },
      ];
    },
    pdfOrgInfo() {
      return [
        `${this.invoice.customer?.phone || 'N/A'}`,
        `${this.invoice.customer?.email || 'N/A'}`,
        `${this.invoice.customer?.address || 'N/A'}`,
      ];
    },
  },

  watch: {
    addProductModal(isOpen) {
      if(isOpen) {
        // Focus quantity field on next tick after modal is rendered
        this.$nextTick(() => {
          const quantityInput = document.querySelector('input[v-model\\.number="invoiceItem.quantity"]');
          if(quantityInput) quantityInput.focus();
        });
      } else {
        // Reset form when modal is closed
        if(this.addProductMode === 'add') {
          this.invoiceItem = {
            invoice_id: this.$route.params.id,
            product_id: '',
            quantity: 1,
            price: 0,
            tax_rate: 0,
            tax: 0
          };
        }
      }
    }
  },
  created: async function () {
    await this.fetchInvoice();
  },
}
</script>

<style scoped>
.invoice-details{ padding:0.5rem; }
</style>
